import React, { useEffect, useState } from 'react';
import { MagicIcon } from '@commonsku/styles';

import { oauth } from '../utils';
import {
  RewriteDescriptionItem,
  RewriteDescriptionProduct,
  RewriteDescriptionResponse,
} from '../services/mockupGeneration/types';

interface Props {
  item: RewriteDescriptionItem;
  product?: RewriteDescriptionProduct;
  locked?: boolean;
  className?: string;
  onRewrite: (description: string) => void;
  onRewritingChange?: (rewriting: boolean) => void;
}

const GENERIC_API_MESSAGES = new Set([
  'application error',
  'internal system error',
]);

const isGenericApiMessage = (msg: unknown): boolean =>
  GENERIC_API_MESSAGES.has(String(msg ?? '').trim().toLowerCase());

const getFirstDetailsError = (errors: unknown): string => {
  if (!Array.isArray(errors) || errors.length === 0) {
    return '';
  }
  const first = errors[0];
  if (first == null) {
    return '';
  }
  if (typeof first === 'string') {
    return first.trim();
  }

  if (typeof first === 'object') {
    // Try common fields that might contain error messages
    const possibleFields =
      (first as { message?: unknown; detail?: unknown; error?: unknown }).message ??
      (first as { detail?: unknown }).detail ??
      (first as { error?: unknown }).error;

    if (possibleFields != null && String(possibleFields).trim()) {
      return String(possibleFields).trim();
    }
  }
  return '';
};

interface ApiErrorJson {
  errors?: unknown;
  message?: unknown;
  error?: unknown;
}

interface ApiError {
  json?: ApiErrorJson;
  message?: string;
}

const getRewriteErrorMessage = (err: ApiError, fallback: string): string => {
  const j = err?.json;
  if (!j) {
    return err?.message || fallback;
  }

  const firstDetail = getFirstDetailsError(j.errors);
  const topMessage = j.message && String(j.message).trim() ? String(j.message).trim() : '';

  if (firstDetail && (!topMessage || isGenericApiMessage(topMessage))) {
    return firstDetail;
  }
  if (topMessage) {
    return topMessage;
  }
  if (firstDetail) {
    return firstDetail;
  }
  if (j.error != null && String(j.error).trim()) {
    return String(j.error);
  }
  return err?.message || fallback;
};

const RewriteDescriptionButton = ({
  item,
  product,
  locked = false,
  className = 'button',
  onRewrite,
  onRewritingChange,
}: Props) => {
  const [rewriting, setRewriting] = useState(false);
  const [error, setError] = useState('');

  useEffect(() => () => {
    onRewritingChange?.(false);
  }, [onRewritingChange]);

  const canRewrite = !!(item.item_description || '').trim();
  const disabled = rewriting || !canRewrite;

  const handleClick = async (e: React.MouseEvent<HTMLAnchorElement>) => {
    e.preventDefault();

    if (disabled) {
      return;
    }

    setError('');
    setRewriting(true);
    onRewritingChange?.(true);

    try {
      const { json } = await oauth(
        'POST',
        'mockup-generation',
        {},
        {
          actionName: 'rewrite_description',
          item_id: item.item_id,
          description: item.item_description || '',
          product_name: item.item_name || product?.product_name || '',
          supplier_name: item.division_name || product?.division_name || product?.supplier_name || '',
          sku: item.item_sku || product?.product_supplier_code || '',
        },
        { logout: false },
        null,
        false,
      ) as { json: RewriteDescriptionResponse };

      const description = (json?.description ?? '').trim();
      if (!description) {
        throw new Error('No rewritten description returned');
      }

      onRewrite(description);
    } catch (err) {
      setError(getRewriteErrorMessage(err as ApiError, 'Could not rewrite description.'));
    } finally {
      setRewriting(false);
      onRewritingChange?.(false);
    }
  };

  const title = !canRewrite ? 'Add a description first' : undefined;

  if (locked) {
    return null;
  }

  return (
    <span style={{ display: 'flex', flex: 1, alignItems: 'center', justifyContent: 'flex-end', gap: '0.5rem' }}>
      <a
        href="#rewrite-description"
        className={className}
        title={title}
        style={{
          margin: 0,
          padding: '0.65rem 1rem',
          opacity: disabled ? 0.5 : 1,
          pointerEvents: disabled ? 'none' : 'auto',
          cursor: disabled ? 'not-allowed' : 'pointer',
          display: 'inline-flex',
          alignItems: 'center',
        }}
        onClick={handleClick}
      >
        <MagicIcon size="small" color="white" style={{ marginRight: 4, verticalAlign: 'middle', flexShrink: 0 }} />
        {rewriting ? 'Rewriting...' : 'Rewrite Description'}
      </a>
      {error ? <span style={{ color: '#EF3340', fontSize: '0.85rem' }}>{error}</span> : null}
    </span>
  );
};

export default RewriteDescriptionButton;
